import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useSettings } from '../../../context/SettingsContext';
import { useTheme } from '../../../context/ThemeContext';
import DashboardLayoutV3 from './DashboardLayoutV3';
import {
  Wallet, TrendingUp, Briefcase, Coins, ArrowUpRight, ArrowDownRight,
  ArrowRightLeft, ChevronRight, Eye, EyeOff, Target, Shield,
  User, Phone, Mail, MessageSquare, Clock, ExternalLink
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Checkbox } from '../../../components/ui/checkbox';
import { toast } from 'sonner';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart as RPieChart, Pie, Cell } from 'recharts';
import Marquee from 'react-fast-marquee';
import PositionsModal from '../../../components/PositionsModal';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = n => n == null || isNaN(n) ? '0,00' : Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function useThemeColors() {
  const { currentTemplate, branding } = useTheme();
  return { ...(currentTemplate?.colors || {}), ...(branding?.customColors || {}) };
}

function buildChart(txs, total) {
  if (!txs?.length) return [{ l: 'Actuel', v: total || 0 }];
  const m = {};
  let r = 0;
  [...txs].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)).forEach(t => {
    const d = new Date(t.created_at);
    const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (['depot', 'deposit', 'interest', 'credit', 'bonus'].includes(t.transaction_type)) r += t.amount || 0;
    else if (['retrait', 'withdrawal', 'debit'].includes(t.transaction_type)) r -= t.amount || 0;
    m[k] = { l: d.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' }), v: Math.max(0, r) };
  });
  const p = Object.values(m);
  if (p.length && total) p.push({ l: 'Actuel', v: total });
  return p.length ? p : [{ l: 'Actuel', v: total || 0 }];
}

function ChartTooltip({ active, payload, label, colors }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="px-3 py-2 rounded-lg text-xs shadow-xl" style={{ background: colors.card || '#111', border: `1px solid ${colors.border || '#222'}` }}>
      <p style={{ color: colors.mutedForeground || '#8A8A8A' }}>{label}</p>
      <p className="font-bold text-base" style={{ color: colors.foreground || '#FFF' }}>{fmt(payload[0]?.value)} EUR</p>
    </div>
  );
}

const PERIODS = [
  { key: "aujourd'hui", label: "Aujourd'hui" }, { key: '7j', label: '7j' }, { key: '14j', label: '14j' },
  { key: '1m', label: '1 mois' }, { key: '3m', label: '3 mois' }, { key: '6m', label: '6 mois' }, { key: '1an', label: '1 an' }
];

export default function ClientDashboardV3() {
  const { user, token, mirrorMode } = useAuth();
  const { platformSettings } = useSettings();
  const { loadMedia, branding } = useTheme();
  const colors = useThemeColors();
  const [searchParams, setSearchParams] = useSearchParams();

  const [stats, setStats] = useState(null);
  const [positions, setPositions] = useState([]);
  const [tradingPerf, setTradingPerf] = useState(null);
  const [crypto, setCrypto] = useState(null);
  const [txs, setTxs] = useState([]);
  const [tPf, setTPf] = useState(null);
  const [news, setNews] = useState([]);
  const [products, setProducts] = useState([]);
  const [profile, setProfile] = useState(null);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hideBalance, setHideBalance] = useState(false);
  const [period, setPeriod] = useState('7j');
  const [ticker, setTicker] = useState([]);

  // Transfer modal
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showPositionsModal, setShowPositionsModal] = useState(false);
  const [transferData, setTransferData] = useState({ amount: '', direction: 'available_to_trading', acceptedTerms: false, acceptedConditions: false });
  const [transferring, setTransferring] = useState(false);

  // Open transfer modal from URL param
  useEffect(() => {
    if (searchParams.get('action') === 'transfer') {
      setShowTransferModal(true);
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => { loadMedia(); }, [loadMedia]);

  const fetchTicker = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/crypto/prices?vs_currency=eur&per_page=20&page=1`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null);
      if (r?.data && Array.isArray(r.data)) setTicker(r.data.map(c => ({ s: String(c.symbol || '').toUpperCase(), p: c.current_price, ch: c.price_change_percentage_24h || 0 })));
    } catch {}
  }, [token]);

  const fetchData = useCallback(async () => {
    if (!user?.id || !token) return;
    try {
      const u = mirrorMode ? `${API}/stats/dashboard?client_id=${user.id}` : `${API}/stats/dashboard`;
      const h = { Authorization: `Bearer ${token}` };
      const [a, b, c, d, e, f, g, i, k, pr] = await Promise.all([
        axios.get(u, { headers: h }),
        axios.get(`${API}/trade-positions/${user.id}?sort_order=desc&limit=20`, { headers: h }).catch(() => ({ data: [] })),
        axios.get(`${API}/trade-positions/${user.id}/performance`, { headers: h }).catch(() => ({ data: null })),
        axios.get(`${API}/transactions/${user.id}?limit=500`, { headers: h }).catch(() => ({ data: { transactions: [] } })),
        axios.get(`${API}/crypto/portfolio`, { headers: h }).catch(() => ({ data: null })),
        axios.get(`${API}/portfolio/trading/${user.id}`, { headers: h }).catch(() => ({ data: null })),
        axios.get(`${API}/news?featured=true&limit=4`, { headers: h }).catch(() => ({ data: { articles: [] } })),
        axios.get(`${API}/placements/products/featured`, { headers: h }).catch(() => ({ data: [] })),
        axios.get(`${API}/partners`).catch(() => ({ data: [] })),
        axios.get(`${API}/clients/${user.id}`, { headers: h }).catch(() => ({ data: null })),
      ]);
      setStats(a.data);
      setPositions(Array.isArray(b.data) ? b.data : (b.data?.positions || []));
      setTradingPerf(c.data);
      setTxs(d.data?.transactions || []);
      setCrypto(e.data);
      setTPf(f.data);
      setNews(g.data?.articles || []);
      setProducts(Array.isArray(i.data) ? i.data : []);
      setPartners(k.data || []);
      setProfile(pr.data);
    } catch (e) {
      console.error('V3:', e);
    } finally {
      setLoading(false);
    }
  }, [user?.id, token, mirrorMode]);

  useEffect(() => {
    fetchData(); fetchTicker();
    const t = setInterval(fetchTicker, 30000);
    const d = setInterval(fetchData, 30000);
    const onVis = () => { if (document.visibilityState === 'visible') fetchData(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(t); clearInterval(d); document.removeEventListener('visibilitychange', onVis); };
  }, [fetchData, fetchTicker]);

  // Transfer handler
  const handleTransferSubmit = async () => {
    if (!transferData.acceptedTerms || !transferData.acceptedConditions) { toast.error('Veuillez accepter les termes et conditions'); return; }
    const amount = parseFloat(transferData.amount);
    if (isNaN(amount) || amount <= 0) { toast.error('Montant invalide'); return; }
    if (transferData.direction === 'available_to_trading' && amount > availableVal) { toast.error(`Solde insuffisant. Disponible: ${availableVal.toFixed(2)}\u20ac`); return; }
    if (transferData.direction === 'trading_to_available' && amount > (tPf?.available || tPf?.balance || 0)) { toast.error('Solde trading insuffisant.'); return; }
    setTransferring(true);
    try {
      await axios.post(`${API}/portfolio/transfer`, { amount, direction: transferData.direction, accepted_terms: true, accepted_conditions: true }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Demande de transfert envoy\u00e9e.');
      setShowTransferModal(false);
      setTransferData({ amount: '', direction: 'available_to_trading', acceptedTerms: false, acceptedConditions: false });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur lors du transfert'); } finally { setTransferring(false); }
  };

  const tVal = tPf?.total_value || tPf?.available || tPf?.balance || 0;
  const cryptoVal = crypto?.total_value_eur || crypto?.total_value || 0;
  const contractsVal = stats?.active_contracts_value || 0;
  const availableVal = stats?.total_available || 0;
  const total = stats ? (availableVal + tVal + cryptoVal + contractsVal) : 0;

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Bonjour';
    if (h < 18) return "Bon apr\u00e8s-midi";
    return 'Bonsoir';
  })();

  const chartData = buildChart(txs, total);
  const primary = colors.primary || '#FFFFFF';
  const muted = colors.mutedForeground || '#8A8A8A';
  const card = colors.card || '#111';
  const border = colors.border || '#1A1A1A';

  const allocationData = [
    { name: 'Disponible', value: availableVal, color: primary },
    { name: 'Trading', value: tVal, color: colors.success || '#4ADE80' },
    { name: 'Placements', value: contractsVal, color: '#A78BFA' },
    { name: 'Crypto', value: cryptoVal, color: '#FBBF24' },
  ].filter(d => d.value > 0);

  const closedPositions = positions.filter(p => p.status === 'closed').slice(0, 8);
  const periodData = tradingPerf?.[period];
  const periodProfit = periodData?.profit || 0;
  const periodTrades = periodData?.trades_count || 0;
  const tradingBase = (tPf?.available || tPf?.balance || 0) - periodProfit;
  const periodPct = tradingBase > 0 ? (periodProfit / tradingBase * 100) : 0;

  const analyst = profile?.analyst;

  if (loading) {
    return (
      <DashboardLayoutV3>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: primary, borderTopColor: 'transparent' }} />
        </div>
      </DashboardLayoutV3>
    );
  }

  return (
    <DashboardLayoutV3>
      {/* Transfer Modal */}
      <PositionsModal open={showPositionsModal} onClose={() => setShowPositionsModal(false)} />

      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowRightLeft className="w-5 h-5 text-purple-500" />{"Transf\u00e9rer des fonds"}</DialogTitle>
            <DialogDescription>{"Transf\u00e9rez des fonds entre vos portefeuilles. Cette demande n\u00e9cessite une validation."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-secondary/30">
              <div><p className="text-sm text-muted-foreground">Fonds Disponibles</p><p className="text-xl font-bold text-emerald-400">{fmt(availableVal)} EUR</p></div>
              <div><p className="text-sm text-muted-foreground">Portefeuille Trading</p><p className="text-xl font-bold text-purple-400">{fmt(tPf?.available || tPf?.balance || 0)} EUR</p></div>
            </div>
            <div>
              <Label>Direction du transfert</Label>
              <Select value={transferData.direction} onValueChange={v => setTransferData(p => ({ ...p, direction: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="available_to_trading">{"Fonds Disponibles \u2192 Trading"}</SelectItem>
                  <SelectItem value="trading_to_available">{"Trading \u2192 Fonds Disponibles"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Montant (EUR)</Label>
              <Input type="number" min="0" step="0.01" value={transferData.amount} onChange={e => setTransferData(p => ({ ...p, amount: e.target.value }))} placeholder="0.00" className="mt-1" />
              <p className="text-xs text-muted-foreground mt-1">{transferData.direction === 'available_to_trading' ? `Max: ${fmt(availableVal)} EUR` : `Max: ${fmt(tPf?.available || tPf?.balance || 0)} EUR`}</p>
            </div>
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center space-x-2"><Checkbox id="v3-terms" checked={transferData.acceptedTerms} onCheckedChange={c => setTransferData(p => ({ ...p, acceptedTerms: c }))} /><Label htmlFor="v3-terms" className="text-sm cursor-pointer">Bon pour accord</Label></div>
              <div className="flex items-center space-x-2"><Checkbox id="v3-cond" checked={transferData.acceptedConditions} onCheckedChange={c => setTransferData(p => ({ ...p, acceptedConditions: c }))} /><Label htmlFor="v3-cond" className="text-sm cursor-pointer">{"J'accepte les termes et conditions"}</Label></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferModal(false)}>Annuler</Button>
            <Button onClick={handleTransferSubmit} disabled={transferring || !transferData.acceptedTerms || !transferData.acceptedConditions || !transferData.amount}>{transferring ? 'Envoi...' : 'Envoyer la demande'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="space-y-5" data-testid="v3-dashboard">

        {/* TICKER */}
        {ticker.length > 0 && (
          <div className="-mx-4 lg:-mx-6 -mt-6 mb-2 py-2" style={{ borderBottom: `1px solid ${border}` }} data-testid="v3-ticker">
            <Marquee speed={30} gradient={false} pauseOnHover>
              {ticker.map((t, i) => (
                <div key={i} className="flex items-center gap-2 mx-8 text-[11px]">
                  <span style={{ color: muted }}>{t.s}</span>
                  <span className="font-medium">{t.p >= 1000 ? t.p.toLocaleString('fr-FR', { maximumFractionDigits: 0 }) : t.p >= 1 ? t.p.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : t.p?.toLocaleString('fr-FR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</span>
                  <span style={{ color: t.ch >= 0 ? (colors.success || '#4ADE80') : (colors.error || '#EF4444') }}>{t.ch >= 0 ? '+' : ''}{t.ch.toFixed(2)}%</span>
                </div>
              ))}
            </Marquee>
          </div>
        )}

        {/* GREETING + TOTAL */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <p className="text-sm" style={{ color: muted }}>{greeting},</p>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{user?.first_name} {user?.last_name}</h1>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-2 justify-end">
              <p className="text-[10px] uppercase tracking-widest font-medium" style={{ color: muted }}>Capital total</p>
              <button onClick={() => setHideBalance(!hideBalance)} className="p-1 rounded" style={{ color: muted }} data-testid="v3-toggle-balance">
                {hideBalance ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
            </div>
            <p className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ color: primary }} data-testid="v3-total-capital">
              {hideBalance ? '******' : `${fmt(total)} EUR`}
            </p>
          </div>
        </div>

        {/* STAT CARDS */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { icon: Wallet, label: 'Disponible', value: availableVal, sub: `Capital: ${fmt(stats?.capital || 0)} EUR`, color: primary, href: '/client/portfolios' },
            { icon: TrendingUp, label: 'Trading', value: tVal, sub: tradingPerf ? `P&L: ${periodProfit >= 0 ? '+' : ''}${fmt(periodProfit)} EUR` : null, color: colors.success || '#4ADE80', href: '/client/trading' },
            { icon: Briefcase, label: 'Placements', value: contractsVal, sub: `${stats?.active_contracts || 0} contrat${(stats?.active_contracts || 0) > 1 ? 's' : ''}`, color: '#A78BFA', href: '/client/my-investments' },
            { icon: Coins, label: 'Crypto', value: cryptoVal, sub: crypto?.holdings?.length ? `${crypto.holdings.length} devise${crypto.holdings.length > 1 ? 's' : ''}` : null, color: '#FBBF24', href: '/client/crypto-portfolio' },
          ].map(s => (
            <Link key={s.label} to={s.href} className="rounded-2xl p-4 border transition-all duration-200 hover:scale-[1.01] group" style={{ background: card, borderColor: border }}>
              <div className="flex items-start justify-between mb-2">
                <div className="p-2 rounded-xl" style={{ background: s.color + '12' }}>
                  <s.icon className="w-4 h-4" style={{ color: s.color }} />
                </div>
                <ChevronRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-50 transition-opacity" />
              </div>
              <p className="text-[11px] font-medium mb-0.5" style={{ color: muted }}>{s.label}</p>
              <p className="text-xl font-bold tracking-tight">{hideBalance ? '****' : `${fmt(s.value)} EUR`}</p>
              {s.sub && <p className="text-[11px] mt-0.5" style={{ color: muted }}>{s.sub}</p>}
            </Link>
          ))}
        </div>

        {/* CHART + ALLOCATION */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-2xl p-5 border" style={{ background: card, borderColor: border }}>
            <p className="text-[10px] font-semibold uppercase tracking-widest mb-4" style={{ color: primary }}>{"Évolution du capital"}</p>
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="v3ChartGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={primary} stopOpacity={0.15} />
                      <stop offset="100%" stopColor={primary} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="l" tick={{ fill: muted, fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: muted, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                  <Tooltip content={<ChartTooltip colors={colors} />} />
                  <Area type="monotone" dataKey="v" stroke={primary} strokeWidth={1.5} fill="url(#v3ChartGrad)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-2xl p-5 border" style={{ background: card, borderColor: border }}>
            <p className="text-[10px] font-semibold uppercase tracking-widest mb-4" style={{ color: primary }}>Allocation</p>
            <div className="h-[140px] flex items-center justify-center">
              {allocationData.length > 0 ? (
                <RPieChart width={140} height={140}>
                  <Pie data={allocationData} cx="50%" cy="50%" innerRadius={40} outerRadius={62} paddingAngle={3} dataKey="value" strokeWidth={0}>
                    {allocationData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                </RPieChart>
              ) : (
                <p className="text-sm" style={{ color: muted }}>{"Aucune donn\u00e9e"}</p>
              )}
            </div>
            <div className="space-y-1.5 mt-3">
              {allocationData.map((d, i) => (
                <div key={i} className="flex items-center justify-between text-[11px]">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ background: d.color }} />
                    <span style={{ color: muted }}>{d.name}</span>
                  </div>
                  <span className="font-medium">{hideBalance ? '****' : `${fmt(d.value)} EUR`}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* VIDEO */}
        {branding?.welcomeVideoEnabled && (branding?.welcomeVideoUrl || branding?.welcomeVideoEmbed || branding?.welcomeVideoFile) && (
          <div className="rounded-2xl border overflow-hidden" style={{ background: card, borderColor: border }}>
            {branding.welcomeVideoTitle && <div className="px-5 pt-4 pb-1"><p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: primary }}>{branding.welcomeVideoTitle}</p></div>}
            <div className="relative overflow-hidden" style={{ height: { small: '150px', medium: '250px', large: '350px', auto: 'auto' }[branding?.welcomeVideoHeight || 'medium'] || '250px', maxHeight: '60vh' }}>
              {branding.welcomeVideoFile ? (
                <video
                  src={branding.welcomeVideoFile.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${branding.welcomeVideoFile}` : branding.welcomeVideoFile}
                  controls playsInline loop={branding.welcomeVideoLoop} autoPlay={branding.welcomeVideoAutoplay} muted={branding.welcomeVideoAutoplay}
                  preload="metadata" className="w-full h-full object-cover" style={{ background: 'black', objectPosition: branding.welcomeVideoObjectPosition || '50% 50%' }} />
              ) : (() => {
                const ap = branding?.welcomeVideoAutoplay ? '&autoplay=1&mute=1' : '';
                const lp = branding?.welcomeVideoLoop ? '&loop=1' : '';
                let ytUrl = null;
                if (branding?.welcomeVideoEmbed) {
                  const m = branding.welcomeVideoEmbed.match(/src="([^"]+)"/);
                  if (m) { const id = m[1].match(/embed\/([^?&"]+)/); if (id) ytUrl = `https://www.youtube-nocookie.com/embed/${id[1]}?modestbranding=1&rel=0&controls=1&playsinline=1${ap}${lp}${lp ? `&playlist=${id[1]}` : ''}`; }
                }
                if (!ytUrl && branding?.welcomeVideoUrl?.includes('youtube')) {
                  const id = branding.welcomeVideoUrl.split('v=')[1]?.split('&')[0];
                  if (id) ytUrl = `https://www.youtube-nocookie.com/embed/${id}?modestbranding=1&rel=0&controls=1&playsinline=1${ap}${lp}${lp ? `&playlist=${id}` : ''}`;
                }
                return ytUrl ? <iframe src={ytUrl} className="absolute inset-0 w-full h-full" style={{ border: 'none' }} allowFullScreen allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" title={branding.welcomeVideoTitle || 'Video'} /> : null;
              })()}
            </div>
          </div>
        )}

        {/* TRADING PERFORMANCE + QUICK ACTIONS */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-2xl p-5 border" style={{ background: card, borderColor: border }}>
            <p className="text-[10px] font-semibold uppercase tracking-widest mb-3" style={{ color: primary }}>{"État du trading"}</p>
            <div className="flex items-center gap-1 mb-4 overflow-x-auto pb-1">
              {PERIODS.map(p => (
                <button key={p.key} onClick={() => setPeriod(p.key)}
                  className="px-3 py-1.5 rounded-lg text-[10px] font-medium whitespace-nowrap transition-all"
                  style={{ background: period === p.key ? primary + '15' : 'transparent', color: period === p.key ? primary : muted, border: `1px solid ${period === p.key ? primary + '30' : border}` }}>
                  {p.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <p className="text-[10px] mb-1" style={{ color: muted }}>Gains / Pertes</p>
                <p className="text-2xl font-bold" style={{ color: periodProfit >= 0 ? (colors.success || '#4ADE80') : (colors.error || '#EF4444') }}>
                  {periodProfit >= 0 ? '+' : ''}{fmt(periodProfit)}
                </p>
                <p className="text-[11px] mt-0.5" style={{ color: periodPct >= 0 ? (colors.success || '#4ADE80') : (colors.error || '#EF4444') }}>
                  ({periodPct >= 0 ? '+' : ''}{periodPct.toFixed(2)}%)
                </p>
              </div>
              <div>
                <p className="text-[10px] mb-1" style={{ color: muted }}>{"Op\u00e9rations"}</p>
                <p className="text-2xl font-bold">{periodTrades}</p>
                <p className="text-[10px] mt-0.5" style={{ color: muted }}>{"sur la p\u00e9riode"}</p>
              </div>
              <div>
                <p className="text-[10px] mb-1" style={{ color: muted }}>Positions actives</p>
                <p className="text-2xl font-bold">{positions.filter(p => ['open', 'visible', 'pending'].includes(p.status)).length}</p>
                <p className="text-[10px] mt-0.5" style={{ color: muted }}>en cours</p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl p-5 border" style={{ background: card, borderColor: border }}>
            <p className="text-[10px] font-semibold uppercase tracking-widest mb-3" style={{ color: primary }}>Actions rapides</p>
            <div className="grid grid-cols-2 gap-2.5">
              {[
                { label: "D\u00e9poser", icon: ArrowUpRight, href: '/client/transactions?action=deposit', color: colors.success || '#4ADE80' },
                { label: 'Retirer', icon: ArrowDownRight, href: '/client/transactions?action=withdraw', color: colors.warning || '#FBBF24' },
                { label: "Transf\u00e9rer", icon: ArrowRightLeft, href: '#transfer', color: '#A78BFA' },
                { label: 'Investir', icon: Target, href: '/client/placements', color: '#60A5FA' },
              ].map(a => a.href === '#transfer' ? (
                <button key={a.label} onClick={() => setShowTransferModal(true)}
                  className="flex flex-col items-center gap-2 py-3 rounded-xl transition-all hover:scale-[1.02]"
                  style={{ background: a.color + '08', border: `1px solid ${a.color}15` }}>
                  <a.icon className="w-4 h-4" style={{ color: a.color }} strokeWidth={1.5} />
                  <span className="text-[10px]" style={{ color: muted }}>{a.label}</span>
                </button>
              ) : (
                <Link key={a.label} to={a.href}
                  className="flex flex-col items-center gap-2 py-3 rounded-xl transition-all hover:scale-[1.02]"
                  style={{ background: a.color + '08', border: `1px solid ${a.color}15` }}>
                  <a.icon className="w-4 h-4" style={{ color: a.color }} strokeWidth={1.5} />
                  <span className="text-[10px]" style={{ color: muted }}>{a.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* POSITIONS TABLE + SIDEBAR */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-2xl p-5 border" style={{ background: card, borderColor: border }}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: primary }}>{"Derni\u00e8res positions"}</p>
              <button onClick={() => setShowPositionsModal(true)} className="text-[10px] flex items-center gap-0.5 cursor-pointer hover:underline" style={{ color: muted }}>Voir tout <ChevronRight className="w-3 h-3" /></button>
            </div>
            {closedPositions.length === 0 ? (
              <div className="py-8 text-center"><TrendingUp className="w-6 h-6 mx-auto mb-2" style={{ color: muted, opacity: 0.5 }} /><p className="text-xs" style={{ color: muted }}>Aucune position</p></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead><tr style={{ color: muted }}><th className="text-left pb-2 font-medium">Actif</th><th className="text-left pb-2 font-medium">Type</th><th className="text-right pb-2 font-medium">Montant</th><th className="text-right pb-2 font-medium">P&L</th><th className="text-right pb-2 font-medium">Date</th><th className="text-right pb-2 font-medium">Statut</th></tr></thead>
                  <tbody>{closedPositions.map((p, i) => {
                    const pl = p.profit || 0;
                    const sym = p.symbol || p.asset || '-';
                    const dt = p.trade_date || (p.close_at ? new Date(p.close_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : '-');
                    return (
                      <tr key={p.id || i} style={{ borderTop: `1px solid ${border}50` }}>
                        <td className="py-2 font-medium">{sym}</td>
                        <td className="py-2" style={{ color: muted }}>{p.trade_type || p.direction || '-'}</td>
                        <td className="py-2 text-right" style={{ color: muted }}>{fmt(p.investment_amount || p.amount || 0)}</td>
                        <td className="py-2 text-right font-medium" style={{ color: pl >= 0 ? (colors.success || '#4ADE80') : (colors.error || '#EF4444') }}>{pl >= 0 ? '+' : ''}{fmt(pl)}</td>
                        <td className="py-2 text-right" style={{ color: muted, fontSize: 10 }}>{dt}</td>
                        <td className="py-2 text-right"><span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: p.status === 'closed' ? `${muted}15` : `${(colors.success || '#4ADE80')}15`, color: p.status === 'closed' ? muted : (colors.success || '#4ADE80') }}>{p.status === 'closed' ? "Ferm\u00e9" : 'Ouvert'}</span></td>
                      </tr>
                    );
                  })}</tbody>
                </table>
              </div>
            )}
          </div>

          {/* SIDEBAR: Analyst + Messages */}
          <div className="space-y-4">
            {/* Analyst Card */}
            <div className="rounded-2xl p-5 border" style={{ background: card, borderColor: border }}>
              <p className="text-[10px] font-semibold uppercase tracking-widest mb-3" style={{ color: primary }}>Votre conseiller</p>
              {analyst ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    {analyst.profile_photo_url ? (
                      <img src={analyst.profile_photo_url} alt="" className="w-10 h-10 rounded-full object-cover" style={{ border: `2px solid ${primary}30` }} />
                    ) : (
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: primary + '15' }}><User className="w-5 h-5" style={{ color: primary }} /></div>
                    )}
                    <div>
                      <p className="text-sm font-medium">{analyst.first_name} {analyst.last_name}</p>
                      {analyst.job_title && <p className="text-[10px]" style={{ color: primary }}>{analyst.job_title}</p>}
                    </div>
                  </div>
                  <div className="space-y-1.5 pt-2" style={{ borderTop: `1px solid ${border}` }}>
                    {analyst.email && <a href={`mailto:${analyst.email}`} className="flex items-center gap-2 text-[11px] hover:underline" style={{ color: muted }}><Mail className="w-3 h-3" />{analyst.email}</a>}
                    {analyst.phone && <a href={`tel:${analyst.phone}`} className="flex items-center gap-2 text-[11px] hover:underline" style={{ color: muted }}><Phone className="w-3 h-3" />{analyst.phone}</a>}
                    {analyst.whatsapp && <a href={`https://wa.me/${analyst.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[11px] hover:underline" style={{ color: '#25D366' }}><MessageSquare className="w-3 h-3" />{analyst.whatsapp}</a>}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: primary + '15' }}><Shield className="w-5 h-5" style={{ color: primary }} /></div>
                  <div><p className="text-sm font-medium">Support Client</p><p className="text-[10px]" style={{ color: muted }}>{"Équipe d\u00e9di\u00e9e"}</p></div>
                </div>
              )}
              <Link to="/messages" className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-[11px] font-medium mt-3 transition-all hover:brightness-110"
                style={{ background: primary + '12', color: primary, border: `1px solid ${primary}20` }}>
                <MessageSquare className="w-3.5 h-3.5" strokeWidth={1.5} />Envoyer un message
              </Link>
            </div>

            {/* Crypto mini */}
            {crypto?.holdings?.length > 0 && (
              <div className="rounded-2xl p-5 border" style={{ background: card, borderColor: border }}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: primary }}>Crypto</p>
                  <Link to="/client/crypto-portfolio" className="text-[10px]" style={{ color: muted }}>Voir</Link>
                </div>
                <div className="space-y-2">
                  {crypto.holdings.slice(0, 4).map((h, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-[8px] font-bold" style={{ background: primary + '15', color: primary }}>
                          {(h.symbol || '?').slice(0, 2).toUpperCase()}
                        </div>
                        <span className="text-[11px] font-medium">{h.symbol?.toUpperCase()}</span>
                      </div>
                      <span className="text-[11px] font-medium">{fmt(h.value_eur || h.value || 0)} EUR</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* NEWS + PRODUCTS */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Products */}
          {products.length > 0 && (
            <div className="lg:col-span-2 rounded-2xl p-5 border" style={{ background: card, borderColor: border }}>
              <div className="flex items-center justify-between mb-4">
                <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: primary }}>Produits d'investissement</p>
                <Link to="/client/placements" className="text-[10px]" style={{ color: muted }}>Explorer</Link>
              </div>
              <div className="space-y-2">
                {products.slice(0, 5).map((p, i) => (
                  <Link key={i} to={`/client/placements/product/${p.id}`}
                    className="flex items-center gap-4 p-3 rounded-xl transition-all hover:brightness-105"
                    style={{ border: `1px solid ${border}` }}>
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: primary + '10' }}>
                      <Briefcase className="w-4 h-4" style={{ color: primary }} strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-medium truncate">{String(p.name || '').trim()}</p>
                      <p className="text-[10px]" style={{ color: muted }}>{String(p.category_name || (typeof p.category === 'object' ? p.category?.title : p.category) || '')}{p.duration_months ? ` · ${p.duration_months} mois` : ''}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-base font-bold" style={{ color: colors.success || '#4ADE80' }}>{String(p.rentability_rate || p.interest_rate || '0')}%</p>
                      <p className="text-[9px]" style={{ color: muted }}>{p.rentability_variable ? `jusqu'\u00e0 ${p.rentability_variable}%` : 'annuel'}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* News + Partners */}
          <div className="space-y-4">
            {news.length > 0 && (
              <div className="rounded-2xl p-5 border" style={{ background: card, borderColor: border }}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: primary }}>{"Actualit\u00e9s"}</p>
                  <Link to="/client/news" className="text-[10px]" style={{ color: muted }}>Tout voir</Link>
                </div>
                <div className="space-y-3">
                  {news.slice(0, 3).map((n, i) => (
                    <Link key={i} to="/client/news" className="flex items-start gap-2.5 group">
                      {n.image_url && <img src={n.image_url} alt="" className="w-10 h-10 rounded object-cover shrink-0 opacity-60 group-hover:opacity-100 transition-opacity" />}
                      <div className="min-w-0">
                        <p className="text-[11px] font-medium line-clamp-2 group-hover:underline">{String(n.title || '')}</p>
                        <p className="text-[9px] mt-0.5" style={{ color: muted }}>{n.created_at ? new Date(n.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : ''}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* PARTENAIRES — pleine largeur */}
        {partners.filter(p => p.is_active !== false).length > 0 && (
          <div className="rounded-2xl p-5 border" style={{ background: card, borderColor: border }}>
            <p className="text-[10px] font-semibold uppercase tracking-widest mb-4" style={{ color: primary }}>Nos partenaires</p>
            <div className="flex flex-wrap items-center gap-6">
              {partners.filter(p => p.is_active !== false).map((p, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  {(p.logo_url || p.logo_base64) ? (
                    <a href={p.website_url || '#'} target="_blank" rel="noreferrer" className="block">
                      <img src={p.logo_base64 || p.logo_url} alt={p.name} className="h-8 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity" style={{ maxWidth: 100 }} />
                    </a>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded flex items-center justify-center" style={{ background: muted + '20' }}>
                        <span className="text-[9px] font-bold" style={{ color: muted }}>{(p.name || '?').slice(0, 2)}</span>
                      </div>
                      <div>
                        <p className="text-[11px] font-medium">{p.name}</p>
                        {p.website_url && <a href={p.website_url} target="_blank" rel="noreferrer" className="text-[9px] flex items-center gap-0.5" style={{ color: muted }}><ExternalLink className="w-2 h-2" />Site</a>}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </DashboardLayoutV3>
  );
}
